import { MinusCircleIcon, PlusCircleIcon } from "@heroicons/react/24/outline";
import FormInputComponent from "@via/components/inputs/form-input-component";
import Modal from "@via/components/modal";
import SelectComponent from "@via/components/select/select";
import { GCPVMInstanceType, WorkbenchInstanceState } from "common";
import { useEffect, useState, type FC } from "react";
import { useForm } from "react-hook-form";
import { ModalSize } from "@via/utils/constants.tsx";

interface EditWorkbenchInstanceModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  workbenchId: number;
  currentName: string | null;
  currentInstanceType: GCPVMInstanceType | undefined;
  currentDiskSize: number | undefined;
  isUpdating: boolean;
  status: WorkbenchInstanceState;
  onSubmit: (
    id: number,
    data: {
      name?: string | null;
      instanceType?: GCPVMInstanceType;
      diskSize?: number;
    }
  ) => Promise<unknown>;
}

const instanceTypeOptions = [
  {
    label: "n2-highmem-2 (2 vCPUs, 16GB RAM)",
    value: "n2-highmem-2",
  },
  {
    label: "n2-highmem-4 (4 vCPUs, 32GB RAM)",
    value: "n2-highmem-4",
  },
  {
    label: "n2-highmem-8 (8 vCPUs, 64GB RAM)",
    value: "n2-highmem-8",
  },
  {
    label: "n2-highmem-16 (16 vCPUs, 128GB RAM)",
    value: "n2-highmem-16",
  },
  {
    label: "n2-highmem-32 (32 vCPUs, 256GB RAM)",
    value: "n2-highmem-32",
  },
  {
    label: "n2-highmem-48 (48 vCPUs, 384GB RAM)",
    value: "n2-highmem-48",
  },
  {
    label: "n2-standard-4 (4 vCPUs, 16GB RAM)",
    value: "n2-standard-4",
  },
  {
    label: "n2-standard-8 (8 vCPUs, 32GB RAM)",
    value: "n2-standard-8",
  },
  {
    label: "n2-standard-16 (16 vCPUs, 64GB RAM)",
    value: "n2-standard-16",
  },
  {
    label: "n2-standard-32 (32 vCPUs, 128GB RAM)",
    value: "n2-standard-32",
  },
  {
    label: "n2-standard-48 (48 vCPUs, 192GB RAM)",
    value: "n2-standard-48",
  },
  {
    label: "n2-standard-64 (64 vCPUs, 256GB RAM)",
    value: "n2-standard-64",
  },
  {
    label: "n2-standard-80 (80 vCPUs, 320GB RAM)",
    value: "n2-standard-80",
  },
  {
    label: "n2-standard-96 (96 vCPUs, 384GB RAM)",
    value: "n2-standard-96",
  },
];

const EditWorkbenchInstanceModal: FC<EditWorkbenchInstanceModalProps> = ({
  open,
  setOpen,
  workbenchId,
  currentName,
  currentInstanceType,
  currentDiskSize,
  status,
  isUpdating,
  onSubmit,
}) => {
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const canEditAdvanced = status === WorkbenchInstanceState.Terminated;

  const { control, handleSubmit, reset } = useForm({
    mode: "onChange",
    defaultValues: {
      name: currentName ?? "",
      instanceType: currentInstanceType ?? GCPVMInstanceType.n2HighMem2,
      diskSize: currentDiskSize ?? 100,
    },
  });

  useEffect(() => {
    // Update the form values when the modal opens
    reset({
      name: currentName ?? "",
      instanceType: currentInstanceType ?? GCPVMInstanceType.n2HighMem2,
      diskSize: currentDiskSize ?? 100,
    });
  }, [currentName, currentInstanceType, currentDiskSize, reset, open]);

  const handleSave = async (payload: {
    name: string;
    instanceType: GCPVMInstanceType;
    diskSize: number;
  }): Promise<void> => {
    const updateData: {
      name?: string | null;
      instanceType?: GCPVMInstanceType;
      diskSize?: number;
    } = {
      name: payload.name || null,
    };

    // Only include instanceType and diskSize if advanced options are visible and workbench is terminated
    if (showAdvancedOptions && canEditAdvanced) {
      updateData.instanceType = payload.instanceType;
      updateData.diskSize = payload.diskSize;
    }

    await onSubmit(workbenchId, updateData);
  };

  // Resolving true hands the close to Modal. Invalid fields resolve false and keep
  // the dialog open, and a failed update rejects, which also keeps it open. The
  // page used to close this dialog itself from a success effect instead.
  const handleConfirmModal = async (): Promise<boolean> => {
    let saved = false;
    await handleSubmit(async payload => {
      await handleSave(payload);
      saved = true;
    })();
    return saved;
  };

  return (
    <Modal
      open={open}
      setOpen={setOpen}
      title="Edit Workbench"
      confirmText="Save"
      onConfirm={handleConfirmModal}
      isConfirmLoading={isUpdating}
      size={ModalSize.Large}>
      <form onSubmit={e => e.preventDefault()}>
        <div className="my-5">
          <FormInputComponent
            name="name"
            label="Workbench Name"
            control={control}
            placeholder="Enter a workbench name"
          />
        </div>

        <div className="flex flex-row items-center mb-4">
          <label className="mr-2 block text-sm font-medium leading-6 text-gray-900">
            Advanced Options
          </label>
          <div onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}>
            {showAdvancedOptions ? (
              <MinusCircleIcon className="h-5 w-5 cursor-pointer text-indigo-600 hover:text-indigo-500" />
            ) : (
              <PlusCircleIcon className="h-5 w-5 cursor-pointer text-indigo-600 hover:text-indigo-500" />
            )}
          </div>
        </div>

        {showAdvancedOptions && (
          <>
            {!canEditAdvanced && (
              <p className="text-sm text-yellow-600 bg-yellow-50 p-3 rounded-md mb-4">
                ⚠️ Instance type and disk size can only be edited when the
                workbench is stopped.
              </p>
            )}

            <div className="my-5">
              <label className="block text-sm font-medium leading-6 text-gray-900 mb-2">
                Instance Type
              </label>
              <SelectComponent
                control={control}
                name="instanceType"
                options={instanceTypeOptions}
                readOnly={!canEditAdvanced}
              />
            </div>

            <div className="my-5">
              <FormInputComponent
                control={control}
                name="diskSize"
                label="Disk Size (GB)"
                type="number"
                min={100}
                max={4096}
                placeholder="GB"
                disabled={!canEditAdvanced}
                onKeyDown={e => {
                  if (e.key === "e" || e.key === "E" || e.key === "+") {
                    e.preventDefault();
                  }
                }}
              />
            </div>
          </>
        )}
      </form>
    </Modal>
  );
};

export default EditWorkbenchInstanceModal;
